import Vue from "vue";
import * as R from "ramda";
// import { getColumnName } from "../../utils";
import uuidv4 from "uuid/v4";
import SongService from "@/services/Song";

const STALE_DATA_TIME = 3600000;

const state = {
  songs: {},
  librarySongs: {
    songIds: [],
    query: "",
    perPage: 0,
    total: 0,
    fetching: false,
    fetchedAt: 0,
    nonPremiumSongsAllowed: 50,
    error: false
  },
  searchResultSongs: {
    songIds: [],
    perPage: 0,
    total: 0,
    fetching: false,
    fetchedAt: 0,
    error: false
  },
  tagSongs: {
    songIds: [],
    tag: "",
    perPage: 0,
    total: 0,
    fetching: false,
    fetchedAt: 0,
    error: false
  },
  homepageSongs: {
    songIds: [],
    fetching: false,
    fetchedAt: 0,
    error: false
  },
  segmentLength: 32,
  wavFile: {
    id: null,
    fetching: false,
    ready: false,
    error: false
  }
};
const createQuantizedSegments = song => {
  let groupedSegments;
  const note = song.notes[0];
  const steps = note.quantizedEndStep - note.quantizedStartStep;
  const quantSteps = steps > 0 ? steps : 1;
  const baseTempo = 120;
  const intervalAdjustment = baseTempo / (song.tempo || 120);
  const noteInterval =
    ((note.endTime - note.startTime) / quantSteps) * intervalAdjustment;
  const createdNotes = [];
  song.notes.forEach(note => {
    const startStepSegment = parseInt(
      note.quantizedStartStep / state.segmentLength
    ); //0
    const endStepSegment = parseInt(
      note.quantizedEndStep / state.segmentLength
    ); //0
    if (startStepSegment !== endStepSegment) {
      const clonedNote = R.clone(note);
      note.quantizedEndStep = (startStepSegment + 1) * state.segmentLength - 1;
      clonedNote.quantizedStartStep =
        (startStepSegment + 2) * state.segmentLength - 1;
      createdNotes.push(clonedNote);
    }
  });
  song.notes.push(...createdNotes);
  const sortFunction = (a, b) => a.quantizedEndStep - b.quantizedEndStep;
  const sortedNotes = R.sort(sortFunction, song.notes);
  const segments = R.groupWith((a, b) => {
    return (
      Math.floor(a.quantizedEndStep / state.segmentLength) ===
      Math.floor(b.quantizedEndStep / state.segmentLength)
    );
  }, sortedNotes);
  groupedSegments = segments.map((segment, index) => {
    const waveformBars = new Array(state.segmentLength / 2);
    waveformBars.fill(1);
    return {
      clipStart: index * (noteInterval * state.segmentLength),
      clipEnd:
        index < segments.length - 1
          ? (index + 1) * (noteInterval * state.segmentLength)
          : segment[segment.length - 1].quantizedEndStep * noteInterval + 1, // trail final clip end by 1 sec
      notes: segment,
      name: index + 1,
      uuid: uuidv4(),
      mockedWaveform: waveformBars.map(() => 100 * Math.random()),
      waveform: null
    };
  });
  return { song, groupedSegments };
};

const createTimeBasedSegments = song => {
  let groupedSegments;
  // Walkerbeats produces note sequence in beats. Each time is 60 bpm at 4/4 so a measure is 4 * 60(bpm)/60(seconds)
  const shiftFactor = 60 / song.tempo;

  // const createdNotes = [];
  // song.notes.forEach(note => {
  //   const startSegment = parseInt(
  //     note.startTime / (state.segmentLength / 2)
  //   );
  //   const endSegment = parseInt(
  //     (note.endTime - 0.0001) / (state.segmentLength / 2)
  //   ); //0
  //   if (startSegment !== endSegment) {
  //     console.log(`start segment: ${startSegment}, end segment: ${endSegment}`);
  //     const clonedNote = R.clone(note);
  //     note.endTime =
  //       (startSegment + 1) * (state.segmentLength / 2);
  //     clonedNote.startTime =
  //       (startSegment + 1) * (state.segmentLength / 2);
  //     createdNotes.push(clonedNote);
  //     console.log(note);
  //     console.log(clonedNote);
  //   }
  // });
  // console.log(createdNotes);
  // song.notes.push(...createdNotes);
  const sortFunction = (a, b) => a.startTime - b.startTime;
  const sortedNotes = R.sort(sortFunction, song.notes);
  const segments = R.groupWith((a, b) => {
    return (
      Math.floor(a.startTime / (state.segmentLength / 2)) ===
      Math.floor(b.startTime / (state.segmentLength / 2))
    );
  }, sortedNotes);
  groupedSegments = segments.map((segment, index) => {
    // const waveformBars = new Array(state.segmentLength / 4);
    // waveformBars.fill(1);
    return {
      clipStart: index * (state.segmentLength / 2) * shiftFactor,
      clipEnd:
        index < segments.length - 1
          ? (index + 1) * (state.segmentLength / 2) * shiftFactor
          : segment[segment.length - 1].endTime + 1, // trail final clip end by 1 sec
      notes: segment,
      name: index + 1,
      uuid: uuidv4(),
      // mockedWaveform: waveformBars.map(() => 100 * Math.random()),
      waveform: null
    };
  });
  return { song, groupedSegments };
};

const mutations = {
  ADD_SONG(state, song) {
    let groupedSegments = [];
    if (song.notes) {
      const note = song.notes[0];
      if (note.quantizedEndStep) {
        const segmentObj = createQuantizedSegments(song);
        song = segmentObj.song;
        groupedSegments = segmentObj.groupedSegments;
      } else {
        const segmentObj = createTimeBasedSegments(song);
        song = segmentObj.song;
        groupedSegments = segmentObj.groupedSegments;
      }
    }
    if (song.styleMeta) {
      song.style = song.styleMeta;
    }
    Vue.set(state.songs, song.id, {
      ...song,
      name: song.name || song.songName,
      duration: song.duration || song.durationMs / 1000,
      songId: song.id,
      groupedSegments,
      selectedSegment: null,
      fetchedAt: Date.now(),
      renderedNotes: null
    });
  },
  UPDATE_SONG(state, song) {
    state.songs[song.id] = song;
  },
  UPDATE_SONG_SEGMENTS(state, song) {
    state.songs[song.id].groupedSegments = song.segments;
  },
  UPDATE_SELECTED_SEGMENT(state, song) {
    state.songs[song.id].selectedSegment = song.selectedSegment;
  },
  CLONE_SEGMENT(state, { id, index }) {
    const segments = state.songs[id].groupedSegments;
    const segment = {
      ...segments[index],
      uuid: uuidv4()
    };
    segments.splice(index, 0, segment);
    state.songs[id].groupedSegments = segments;
  },
  DELETE_SEGMENT(state, { id, index }) {
    const segments = state.songs[id].groupedSegments;
    segments.splice(index, 1);
    state.songs[id].groupedSegments = segments;
  },
  UPDATE_SEGMENT_LENGTH(state, length) {
    state.segmentLength = length;
  },
  EXPORT_NOTESEQUENCE(state, id) {
    const song = state.songs[id];
    const segmentLength = state.segmentLength;
    if (song) {
      const note = song.notes[0];
      if (note.quantizedEndStep) {
        song.renderedNotes = [];
        song.groupedSegments.forEach((group, index) => {
          const originalIndex = group.name - 1;
          group.notes.forEach(note => {
            const clonedNote = R.clone(note);
            const baseStartStep =
              note.quantizedStartStep - originalIndex * segmentLength;
            clonedNote.quantizedStartStep =
              baseStartStep + index * segmentLength;
            const baseEndStep =
              note.quantizedEndStep - originalIndex * segmentLength;
            clonedNote.quantizedEndStep = baseEndStep + index * segmentLength;
            song.renderedNotes.push(clonedNote);
          });
        });
      } else {
        song.renderedNotes = [];
        song.groupedSegments.forEach((group, index) => {
          const originalIndex = group.name - 1;
          group.notes.forEach(note => {
            const clonedNote = R.clone(note);
            const baseStartTime =
              note.startTime - originalIndex * (segmentLength / 2);
            clonedNote.startTime = baseStartTime + index * (segmentLength / 2);
            const baseEndTime =
              note.endTime - originalIndex * (segmentLength / 2);
            clonedNote.endTime = baseEndTime + index * (segmentLength / 2);
            song.renderedNotes.push(clonedNote);
          });
        });
      }
    }
  },
  CLEAR_RENDERED_NOTES(state, id) {
    const song = state.songs[id];
    if (song) {
      song.renderedNotes = null;
    }
  },
  CLEAR_MERGE_TRACK(state, id) {
    const song = state.songs[id];
    if (song) {
      song.mergedMp3File = null;
    }
  },
  REMOVE_LIBRARY_SONG(state, songId) {
    const index = state.librarySongs.songIds.indexOf(songId);
    if (index !== -1) {
      state.librarySongs.songIds.splice(index, 1);
      state.librarySongs.total -= 1;
      state.librarySongs.fetchedAt = 0;
    }
  },
  INSERT_LIBRARY_SONG(state, song) {
    Vue.set(state.songs, song.id, {
      ...song,
      songName: song.name || song.songName,
      duration: song.duration || song.durationMs / 1000,
      id: song.id,
      saved: true,
      fetchedAt: Date.now()
    });
    state.librarySongs.fetchedAt = 0;
    state.librarySongs.songIds.unshift(song.id);
    state.librarySongs.total += 1;
  },
  ADD_LIBRARY_SONGS(state, data) {
    if (data.results && data.results.length > 0) {
      for (let i = 0; i < data.results.length; i++) {
        const song = data.results[i];
        Vue.set(state.songs, song.id, {
          ...song,
          songName: song.name || song.songName,
          id: song.id,
          saved: true,
          fetchedAt: data.fetchedAt
        });
      }
      const songIds = data.results.map(song => song.id);
      if (
        data.pagination.perPage !== state.librarySongs.perPage ||
        data.pagination.total !== state.librarySongs.total
      ) {
        state.librarySongs.songIds = new Array(
          parseInt(data.pagination.total)
        ).fill("");
        state.librarySongs.songIds.splice(
          (data.pagination.page - 1) * state.librarySongs.perPage,
          data.results.length,
          ...songIds
        );
        state.librarySongs.perPage = data.pagination.perPage;
        state.librarySongs.total = data.pagination.total;
        state.librarySongs.fetchedAt = data.fetchedAt;
      } else {
        state.librarySongs.songIds.splice(
          (data.pagination.page - 1) * state.librarySongs.perPage,
          data.results.length,
          ...songIds
        );
      }
    }
  },
  SET_LIBRARY_SONGS_FETCHING(state, value) {
    state.librarySongs.fetching = value;
  },
  SET_LIBRARY_SONGS_ERROR(state, hasError) {
    state.librarySongs.error = hasError;
  },
  SET_LIBRARY_SONGS_QUERY(state, query) {
    state.librarySongs.query = query;
  },
  RESET_LIBRARY_CACHE(state) {
    state.librarySongs.fetchedAt = 0;
  },
  RESET_LIBRARY(state) {
    state.librarySongs = {
      songIds: [],
      query: "",
      perPage: 0,
      total: 0,
      fetching: false,
      fetchedAt: 0,
      nonPremiumSongsAllowed: 50,
      error: false
    };
  },
  MARK_SONG_SAVED(state, id) {
    const song = state.songs[id];
    if (song) {
      song.saved = true;
      state.librarySongs.fetchedAt = 0; //bust cache.
    }
  },
  UPDATE_SONG_DETAILS(
    state,
    { songId, songName, artistName, tags, isPrivate }
  ) {
    const song = state.songs[songId];
    if (song) {
      song.name = songName;
      song.songName = songName;
      song.artistName = artistName;
      song.tags = tags;
      song.private = isPrivate;
    }
  },
  ADD_WAVEFORM(state, { songId, waveformData }) {
    const song = state.songs[songId];
    const segmentDivider = state.segmentLength / 2;
    if (song && song.groupedSegments) {
      song.groupedSegments.forEach((group, index) => {
        group.waveform = waveformData
          .slice(index * segmentDivider, (index + 1) * segmentDivider)
          .map(value => value * 200);
      });
    }
  },
  ADD_SEARCH_RESULT_SONGS({ searchResultSongs }, data) {
    if (data.results && data.results.length > 0) {
      for (let i = 0; i < data.results.length; i++) {
        const song = data.results[i];
        Vue.set(state.songs, song.id, {
          ...song,
          songName: song.name || song.songName,
          id: song.id,
          saved: true,
          fetchedAt: data.fetchedAt
        });
      }
      const songIds = data.results.map(song => song.id);
      if (
        data.pagination.perPage !== searchResultSongs.perPage ||
        data.pagination.total !== searchResultSongs.total
      ) {
        searchResultSongs.songIds = new Array(
          parseInt(data.pagination.total)
        ).fill("");
        searchResultSongs.songIds.splice(
          (data.pagination.page - 1) * searchResultSongs.perPage,
          data.results.length,
          ...songIds
        );
        searchResultSongs.perPage = data.pagination.perPage;
        searchResultSongs.total = data.pagination.total;
        searchResultSongs.fetchedAt = data.fetchedAt;
      } else {
        searchResultSongs.songIds.splice(
          (data.pagination.page - 1) * searchResultSongs.perPage,
          data.results.length,
          ...songIds
        );
      }
    }
  },
  SET_SEARCH_RESULT_SONGS_FETCHING(state, value) {
    state.searchResultSongs.fetching = value;
  },
  SET_SEARCH_RESULT_SONGS_ERROR(state, hasError) {
    state.searchResultSongs.error = hasError;
  },
  RESET_SEARCH_RESULT_SONGS_CACHE(state) {
    state.searchResultSongs.fetchedAt = 0;
  },
  RESET_SEARCH_RESULT_SONGS(state) {
    state.searchResultSongs = {
      songIds: [],
      perPage: 0,
      total: 0,
      fetchedAt: 0
    };
  },
  ADD_TAG_SONGS({ tagSongs }, data) {
    if (data.results && data.results.length > 0) {
      for (let i = 0; i < data.results.length; i++) {
        const song = data.results[i];
        Vue.set(state.songs, song.id, {
          ...song,
          songName: song.name || song.songName,
          id: song.id,
          saved: true,
          fetchedAt: data.fetchedAt
        });
      }
      const songIds = data.results.map(song => song.id);
      if (
        data.pagination.perPage !== tagSongs.perPage ||
        data.pagination.total !== tagSongs.total
      ) {
        tagSongs.songIds = new Array(parseInt(data.pagination.total)).fill("");
        tagSongs.songIds.splice(
          (data.pagination.page - 1) * tagSongs.perPage,
          data.results.length,
          ...songIds
        );
        tagSongs.perPage = data.pagination.perPage;
        tagSongs.total = data.pagination.total;
        tagSongs.fetchedAt = data.fetchedAt;
      } else {
        tagSongs.songIds.splice(
          (data.pagination.page - 1) * tagSongs.perPage,
          data.results.length,
          ...songIds
        );
      }
    }
  },
  SET_TAG_SONGS_FETCHING(state, value) {
    state.tagSongs.fetching = value;
  },
  SET_TAG_SONGS_ERROR(state, hasError) {
    state.tagSongs.error = hasError;
  },
  RESET_TAG_SONGS(state) {
    state.tagSongs = {
      songIds: [],
      tag: "",
      perPage: 0,
      total: 0,
      fetching: false,
      fetchedAt: 0,
      error: false
    };
  },
  ADD_HOMEPAGE_SONGS({ homepageSongs, songs }, data) {
    if (data.results && data.results.length > 0) {
      for (let i = 0; i < data.results.length; i++) {
        const song = data.results[i];
        Vue.set(songs, song.id, {
          ...song,
          songName: song.name || song.songName,
          name: song.name || song.songName,
          duration: song.duration || song.durationMs / 1000,
          id: song.id,
          saved: true,
          fetchedAt: data.fetchedAt
        });
      }
      const songIds = data.results.map(song => song.id);
      homepageSongs.songIds = songIds;
      homepageSongs.fetchedAt = data.fetchedAt;
    }
  },
  SET_HOMEPAGE_SONGS_FETCHING(state, value) {
    state.homepageSongs.fetching = value;
  },
  SET_HOMEPAGE_SONGS_ERROR(state, hasError) {
    state.homepageSongs.error = hasError;
  },
  SET_WAV_FILE_FETCHING({ wavFile }, id) {
    wavFile.id = id;
    wavFile.fetching = true;
    wavFile.ready = false;
    wavFile.error = false;
    wavFile.started = Date.now();
  },
  SET_WAV_FILE_READY({ wavFile }) {
    wavFile.fetching = false;
    wavFile.ready = true;
    wavFile.error = false;
  },
  SET_WAV_FILE_ERROR({ wavFile }) {
    wavFile.fetching = false;
    wavFile.ready = false;
    wavFile.error = true;
  },
  RESET_WAV_FILE({ wavFile }) {
    wavFile.id = null;
    wavFile.feady = false;
    wavFile.error = false;
    wavFile.fetching = false;
    wavFile.started = 0;
  }
};
const actions = {
  async fetchLibrarySongs(context, { shouldReset, ...params }) {
    context.commit("SET_LIBRARY_SONGS_FETCHING", true);
    const res = await SongService.getUserSongs(params);
    context.commit("SET_LIBRARY_SONGS_FETCHING", false);
    if (res.status === 200 && res.data) {
      if (res.data.results.length === 0 && shouldReset) {
        context.commit("RESET_LIBRARY");
      } else {
        context.commit("ADD_LIBRARY_SONGS", res.data);
      }
      context.commit("SET_LIBRARY_SONGS_ERROR", false);
    } else {
      context.commit("SET_LIBRARY_SONGS_ERROR", true);
    }
  },
  addSong(context, song) {
    context.commit("ADD_SONG", song);
  },
  updateSong(context, song) {
    context.commit("UPDATE_SONG", song);
  },
  cloneSegment(context, song) {
    context.commit("CLONE_SEGMENT", song);
  },
  deleteSegment(context, song) {
    context.commit("DELETE_SEGMENT", song);
  },
  updateSegmentLength(context, length) {
    context.commit("UPDATE_SEGMENT_LENGTH", length);
  },
  insertLibrarySong({ commit }, song) {
    commit("INSERT_LIBRARY_SONG", song);
  },
  removeLibrarySong({ commit }, songId) {
    commit("REMOVE_LIBRARY_SONG", songId);
  },
  addLibrarySongs(context, data) {
    context.commit("ADD_LIBRARY_SONGS", { ...data, fetchedAt: Date.now() });
  },
  setLibrarySongsQuery(context, query) {
    context.commit("SET_LIBRARY_SONGS_QUERY", query);
  },
  resetLibraryCache(context) {
    context.commit("RESET_LIBRARY_CACHE");
  },
  resetLibrary(context) {
    context.commit("RESET_LIBRARY");
  },
  markSongPlayed(context, id) {
    context.commit("MARK_SONG_PLAYED", id);
  },
  markAllPlayed(context) {
    context.commit("MARK_ALL_PLAYED");
  },
  exportNotesequence(context, id) {
    context.commit("EXPORT_NOTESEQUENCE", id);
  },
  clearRenderedNotes(context, id) {
    context.commit("CLEAR_RENDERED_NOTES", id);
  },
  markSongSaved(context, id) {
    context.commit("MARK_SONG_SAVED", id);
  },
  updateSongDetails(context, { songId, songName, artistName }) {
    context.commit("UPDATE_SONG_DETAILS", { songId, songName, artistName });
  },
  addWaveformData(context, { songId, waveformData }) {
    context.commit("ADD_WAVEFORM", { songId, waveformData });
  },
  async fetchSearchResultSongs({ commit }, { shouldReset, ...params }) {
    commit("SET_SEARCH_RESULT_SONGS_FETCHING", true);
    const res = await SongService.getSongs(params);
    commit("SET_SEARCH_RESULT_SONGS_FETCHING", false);
    if (res.status === 200 && res.data) {
      if (res.data.results.length === 0 && shouldReset) {
        commit("RESET_SEARCH_RESULT_SONGS");
      } else {
        commit("ADD_SEARCH_RESULT_SONGS", {
          ...res.data,
          fetchedAt: Date.now()
        });
        commit("SET_SEARCH_RESULT_SONGS_ERROR", false);
      }
    } else {
      commit("SET_SEARCH_RESULT_SONGS_ERROR", true);
    }
  },
  async fetchTagSongs({ commit }, { shouldReset, ...params }) {
    commit("SET_TAG_SONGS_FETCHING", true);
    const res = await SongService.getSongsForTag(params);
    commit("SET_TAG_SONGS_FETCHING", false);
    if (res.status === 200 && res.data) {
      if (res.data.results.length === 0 && shouldReset) {
        commit("RESET_TAG_SONGS");
      } else {
        commit("ADD_TAG_SONGS", {
          ...res.data,
          fetchedAt: Date.now()
        });
        commit("SET_TAG_SONGS_ERROR", false);
      }
    } else {
      commit("SET_TAG_SONGS_ERROR", true);
    }
  },
  async fetchHomepageTracks({ commit }) {
    commit("SET_HOMEPAGE_SONGS_FETCHING", true);
    const res = await SongService.getHomepageTracks();
    commit("SET_HOMEPAGE_SONGS_FETCHING", false);
    if (res.status === 200 && res.data) {
      commit("ADD_HOMEPAGE_SONGS", {
        results: res.data,
        fetchedAt: Date.now()
      });
      commit("SET_HOMEPAGE_SONGS_ERROR", false);
    } else {
      commit("SET_HOMEPAGE_SONGS_ERROR", true);
    }
  },
  addSongResults({ commit }, data) {
    commit("ADD_SEARCH_RESULT_SONGS", {
      ...data,
      fetchedAt: Date.now()
    });
  },
  resetSongResultsCache({ commit }) {
    commit("RESET_SEARCH_RESULT_SONGS_CACHE");
  },
  resetSongResults({ commit }) {
    commit("RESET_SEARCH_RESULT_SONGS");
  },
  async updateSongInfo(
    { commit },
    { songId, title, artistName, isPrivate, tags }
  ) {
    return new Promise((resolve, reject) => {
      SongService.putSaveSong({
        songId,
        songName: title,
        artistName,
        isPrivate,
        tags
      }).then(
        result => {
          if (result.status === 200) {
            commit("UPDATE_SONG_DETAILS", {
              songId,
              songName: title,
              artistName,
              isPrivate,
              tags
            });
          }
          resolve(result);
        },
        error => {
          console.log(error);
          reject(error);
        }
      );
    });
  },
  async saveSong({ dispatch, state }, songId) {
    return new Promise((resolve, reject) => {
      SongService.postSaveSong({ songId }).then(
        result => {
          if (result.status === 200 && result.data) {
            dispatch("resetLibraryCache");
            dispatch("markSongSaved", songId);
            dispatch("user/getUserOnboardingSteps", {}, { root: true });
          }
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },
  clearMergeTrack({ commit, state }, songId) {
    //TODO: call the server and clear the mergefile
    commit("CLEAR_MERGE_TRACK", songId);
    SongService.deleteVocal(songId);
  },
  async generateWavFile({ commit, dispatch, state }, songId) {
    commit("SET_WAV_FILE_FETCHING", songId);
    const res = await SongService.requestWav(songId);
    if (res.status && res.data) {
      const wavStatus = res.data.status;
      if (wavStatus === "ready") {
        commit("SET_WAV_FILE_READY");
      } else {
        dispatch("getWavStatus");
      }
    }
  },
  async getWavStatus({ dispatch, commit, state }) {
    const songId = state.wavFile.id;
    const ready = state.wavFile.ready;
    if (ready) {
      return;
    }
    const timedOut = Date.now() - state.wavFile.started >= 120000;
    if (timedOut) {
      commit("SET_WAV_ERROR");
      return;
    }
    const res = await SongService.getWavStatus(songId);
    if (res.status === 200) {
      const wavReady = res.data;
      if (wavReady) {
        commit("SET_WAV_FILE_READY");
      } else {
        setTimeout(() => {
          dispatch("getWavStatus");
        }, 2000);
      }
    }
  }
};
const filterPlayedFalse = list => R.filter(R.propEq("played", false), list);
const getters = {
  getSongById: state => id => state.songs[id],
  getSongSegmentsById: state => id =>
    state.songs[id] ? state.songs[id].groupedSegments : [],
  getSegmentLength: state => state.segmentLength,
  getLibrarySongsForPage: state => page => {
    const nonPremiumSongsAllowed = state.librarySongs.nonPremiumSongsAllowed;
    const totalSongs = state.librarySongs.total;

    const songIds = state.librarySongs.songIds.slice(
      (page - 1) * state.librarySongs.perPage,
      page * state.librarySongs.perPage
    );
    let songs = [];
    songIds.forEach((id, index) => {
      const songNum = index + 1 + (page - 1) * state.librarySongs.perPage;
      const locked = songNum <= totalSongs - nonPremiumSongsAllowed;
      const song = state.songs[id];
      if (song) {
        songs.push({
          ...song,
          locked: locked
        });
      }
    });
    return songs;
  },
  getLibrarySongs: state => {
    const nonPremiumSongsAllowed = state.librarySongs.nonPremiumSongsAllowed;
    const totalSongs = state.librarySongs.total;

    const songIds = state.librarySongs.songIds;
    let songs = [];
    songIds.forEach((id, index) => {
      const songNum = index + 1;
      const locked = songNum <= totalSongs - nonPremiumSongsAllowed;
      const song = state.songs[id];
      if (song) {
        songs.push({
          ...song,
          locked: locked
        });
      }
    });
    return songs;
  },
  getNamedLibrarySongs: state => {
    const songIds = state.librarySongs.songIds;
    let songs = [];
    songIds.forEach((id, index) => {
      const songNum = index + 1;
      const song = state.songs[id];
      if (song && song.songName && song.songName != "New Song") {
        songs.push({
          ...song
        });
      }
    });
    return songs;
  },
  getLibrarySongsCount: state => state.librarySongs.songIds.length,
  getLibrarySongsTotalPages: state =>
    Math.ceil(state.librarySongs.total / state.librarySongs.perPage),
  getLibrarySongsTotal: state => state.librarySongs.total,
  getLibrarySongsFetchedAt: state => state.librarySongs.fetchedAt,
  fetchingLibrarySongs: state => state.librarySongs.fetching,
  getLibrarySongsQuery: state => state.librarySongs.query,
  getLibrarySongsStale: state =>
    Date.now() - state.librarySongs.fetchedAt > STALE_DATA_TIME,
  getRenderedNotesById: state => id => {
    const song = state.songs[id];
    return song ? song.renderedNotes : null;
  },
  getSearchResultSongs: state => {
    const { songIds } = state.searchResultSongs;
    return songIds.map(id => state.songs[id]).filter(item => item);
  },
  getSearchResultSongsTotal: state => state.searchResultSongs.total,
  fetchingSearchResultSongs: state => state.searchResultSongs.fetching,
  getTagSongs: state => {
    const { songIds } = state.tagSongs;
    return songIds.map(id => state.songs[id]).filter(item => item);
  },
  getTagSongsTotal: state => state.tagSongs.total,
  fetchingTagSongs: state => state.tagSongs.fetching,
  getHomepageTracks: state => {
    const { songIds } = state.homepageSongs;
    return songIds.map(id => state.songs[id]).filter(item => item);
  },
  fetchingHomepageTracks: state => state.homepageSongs.fetching,
  // getUnplayedMatchingSongs: state => request => R.reject(R.not(R.equals(request, )), filterPlayedFalse(state.songs))
  getWavFileReady: state => state.wavFile.ready,
  getWavFileId: state => state.wavFile.id
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
